import { useState, useEffect } from 'react'
import { 
  PlusIcon, 
  FunnelIcon, 
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'
import { api } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { useStats } from '../contexts/StatsContext'
import AddTradeModal from '../components/AddTradeModal'
import toast from 'react-hot-toast'

const Trades = () => {
  const [filters, setFilters] = useState({
    symbol: '',
    status: '',
    startDate: '',
    endDate: '',
    strategy: ''
  })
  const [page, setPage] = useState(1)
  const [showFilters, setShowFilters] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [trades, setTrades] = useState([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 1
  })
  const { user } = useAuth()
  const { refreshStats } = useStats()

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        setLoading(true)
        const params = {
          page,
          limit: 50,
          ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
        }
        
        const response = await api.trades.getAll(params)
        setTrades(response.data.trades || [])
        setPagination(response.data.pagination || {
          page: 1,
          limit: 50,
          total: 0,
          pages: 1
        })
        setLoading(false)
      } catch (error) {
        console.error('Error fetching trades:', error)
        setTrades([])
        setLoading(false)
      }
    }

    if (user) {
      fetchTrades()
    }
  }, [user, page, filters])

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPage(1)
  }

  const clearFilters = () => {
    setFilters({
      symbol: '',
      status: '',
      startDate: '',
      endDate: '',
      strategy: ''
    })
    setPage(1)
  }

  const handleAddTrade = async (tradeData) => {
    try {
      // Validate required fields
      if (!tradeData.symbol || !tradeData.quantity || !tradeData.entryPrice || !tradeData.entryDate) {
        toast.error('Please fill in all required fields')
        return
      }

      const formattedData = {
        symbol: tradeData.symbol.toUpperCase(),
        assetType: 'stock', // Default to stock for now
        side: tradeData.side,
        quantity: parseFloat(tradeData.quantity),
        entryPrice: parseFloat(tradeData.entryPrice),
        exitPrice: tradeData.exitPrice ? parseFloat(tradeData.exitPrice) : undefined,
        entryTime: new Date(tradeData.entryDate + 'T12:00:00.000Z').toISOString(),
        exitTime: tradeData.exitDate ? new Date(tradeData.exitDate + 'T12:00:00.000Z').toISOString() : undefined,
        strategy: tradeData.strategy || undefined,
        notes: tradeData.notes || undefined,
        commission: 0,
        fees: 0
      }

      console.log('Sending trade data:', formattedData)

      await api.trades.create(formattedData)
      toast.success('Trade added successfully!')
      
      // Refresh trades list
      const params = {
        page,
        limit: 50,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      }
      
      const response = await api.trades.getAll(params)
      setTrades(response.data.trades || [])
      setPagination(response.data.pagination || {
        page: 1,
        limit: 50,
        total: 0,
        pages: 1
      })

      // Refresh sidebar stats
      refreshStats()
    } catch (error) {
      console.error('Error adding trade:', error)
      const errorMessage = error.response?.data?.error || 'Failed to add trade. Please try again.'
      toast.error(errorMessage)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="relative min-h-full">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse"></div>
      </div>
      
      <div className="relative p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-white via-indigo-200 to-purple-200 bg-clip-text text-transparent">
                Trades 📊
              </h1>
              <p className="text-gray-300 mt-1">
                Track and analyze all your trading activity
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 backdrop-blur-xl transition-all"
              >
                <FunnelIcon className="w-4 h-4 mr-2" />
                Filters
              </button>
              
              <button className="flex items-center px-4 py-2 text-sm font-medium text-white bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 backdrop-blur-xl transition-all">
                <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                Export
              </button>
              
              <button 
                onClick={() => setShowAddModal(true)}
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg hover:from-indigo-600 hover:to-purple-700 shadow-lg transition-all"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Trade
              </button>
            </div>
          </div>

        {/* Filters */}
        {showFilters && (
          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 mb-8 shadow-2xl">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Symbol</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="e.g. AAPL, TSLA"
                    value={filters.symbol}
                    onChange={(e) => handleFilterChange('symbol', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-white placeholder-gray-400 backdrop-blur-xl"
                  />
                  <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-white backdrop-blur-xl"
                >
                  <option value="">All Status</option>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                  <option value="partial">Partial</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Start Date</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-white backdrop-blur-xl"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">End Date</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-white backdrop-blur-xl"
                />
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 backdrop-blur-xl transition-all"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Trades Table */}
        <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
            </div>
          ) : trades.length === 0 ? (
            <div className="text-center py-12">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No trades found</h3>
              <p className="text-gray-300 mb-6">
                {Object.values(filters).some(v => v) 
                  ? "Try adjusting your filters or add your first trade."
                  : "Start by adding your first trade or connecting a broker for automatic import."
                }
              </p>
              <button 
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Trade
              </button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-black/20">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Symbol
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Side
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Entry Price
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Exit Price
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        P&L
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Entry Time
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {trades.map((trade) => (
                      <tr key={trade.id} className="hover:bg-white/5 transition-colors duration-200">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="text-sm font-medium text-white">
                              {trade.symbol}
                            </div>
                            <div className="text-xs text-gray-400 ml-2">
                              {trade.asset_type}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${
                            trade.side === 'buy' 
                              ? 'bg-green-500/20 text-green-400 border-green-500/30'
                              : 'bg-red-500/20 text-red-400 border-red-500/30'
                          }`}>
                            {trade.side?.toUpperCase() || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {(trade.quantity || 0).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {formatCurrency(trade.entry_price)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {trade.exit_price ? formatCurrency(trade.exit_price) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm">
                            <div className={`font-bold ${
                              (trade.pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {formatCurrency(trade.pnl || 0)}
                            </div>
                            {trade.pnl_percent && (
                              <div className={`text-xs ${
                                trade.pnl_percent >= 0 ? 'text-green-400' : 'text-red-400'
                              }`}>
                                {trade.pnl_percent > 0 ? '+' : ''}{trade.pnl_percent.toFixed(2)}%
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          <div>
                            <div>{formatDate(trade.entry_time)}</div>
                            <div className="text-xs text-gray-400">
                              {formatTime(trade.entry_time)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${
                            trade.status === 'closed' 
                              ? 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                              : trade.status === 'open'
                              ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                              : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                          }`}>
                            {trade.status?.toUpperCase() || 'UNKNOWN'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          <div className="flex items-center space-x-2">
                            <button className="text-indigo-400 hover:text-indigo-300">
                              View
                            </button>
                            <button className="text-gray-400 hover:text-gray-300">
                              Edit
                            </button>
                            <button className="text-indigo-400 hover:text-indigo-300">
                              <ChartBarIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="bg-black/20 px-4 py-3 border-t border-white/10 sm:px-6 mt-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-white/20 text-sm font-medium rounded-md text-gray-300 bg-white/10 hover:bg-white/20 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                      disabled={page === pagination.pages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-white/20 text-sm font-medium rounded-md text-gray-300 bg-white/10 hover:bg-white/20 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-300">
                        Showing{' '}
                        <span className="font-medium">
                          {(pagination.page - 1) * pagination.limit + 1}
                        </span>{' '}
                        to{' '}
                        <span className="font-medium">
                          {Math.min(pagination.page * pagination.limit, pagination.total)}
                        </span>{' '}
                        of{' '}
                        <span className="font-medium">{pagination.total}</span>{' '}
                        results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => setPage(Math.max(1, page - 1))}
                          disabled={page === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-white/20 bg-white/10 text-sm font-medium text-gray-300 hover:bg-white/20 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        {[...Array(Math.min(5, pagination.pages))].map((_, i) => {
                          const pageNum = i + 1
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setPage(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === pageNum
                                  ? 'z-10 bg-indigo-500/20 border-indigo-500/30 text-indigo-400'
                                  : 'bg-white/10 border-white/20 text-gray-300 hover:bg-white/20'
                              }`}
                            >
                              {pageNum}
                            </button>
                          )
                        })}
                        <button
                          onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                          disabled={page === pagination.pages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-white/20 bg-white/10 text-sm font-medium text-gray-300 hover:bg-white/20 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            )}
            </>
          )}
        </div>

        {/* Add Trade Modal */}
        <AddTradeModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddTrade}
        />
        </div>
      </div>
    </div>
  )
}

export default Trades